/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignSchedule, StudioCampaign, StudioChannel } from "../types";
import { STUDIO_CHANNELS } from "../types";
import { CHANNEL_COST_CENTS, STUB_SEGMENTS } from "../stub";
import { CHANNEL_META, Field, formatMoney, Segmented, useOfferText, usePreview } from "../shared";
import { checkCampaign, estimateReach, type CampaignCheck, type CheckLevel } from "../checks";
import { smsSegments } from "../variables";
import { renderSms } from "../channels/SmsEditor";

type ReviewStepProps = {
  campaign: StudioCampaign;
  onChange: (patch: Partial<StudioCampaign>) => void;
  onGoTo: (step: number) => void;
};

const LEVEL_ICON: Record<CheckLevel, { icon: string; tone: string }> = {
  error: { icon: "error", tone: "text-error" },
  warning: { icon: "warning", tone: "text-warning" },
  ok: { icon: "check_circle", tone: "text-success" },
};

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

// Builder step that fixes a check; null when it is fixed on this step or in the header.
const stepForCheck = (c: CampaignCheck): number | null => {
  if (c.key === "coupon_without_offer" || c.key === "email_no_coupon") return 1;
  if (c.channel && c.key !== "nobody_reachable") return 2;
  if (c.key === "no_channels" || c.key === "nobody_reachable" || c.key === "no_holdout") return 0;
  return null;
};

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

export const ReviewStep = defineComponent<ReviewStepProps>({
  name: "CampaignReviewStep",
  props: ["campaign", "onChange", "onGoTo"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();
    const offerText = useOfferText();
    const testRecipient = ref("");
    const testSent = ref<string | null>(null);

    const segment = computed(() => STUB_SEGMENTS.find((s) => s.id === props.campaign.segment_id) ?? STUB_SEGMENTS[0]);
    const reach = computed(() => estimateReach(props.campaign, segment.value));
    const checks = computed(() => checkCampaign(props.campaign, segment.value));

    const costFor = (ch: StudioChannel) => {
      const count = reach.value.messages[ch] ?? 0;
      const perMessage = ch === "sms" ? smsSegments(renderSms(props.campaign.sms, preview.render)).segments * CHANNEL_COST_CENTS.sms : CHANNEL_COST_CENTS[ch];
      return count * perMessage;
    };
    const totalCost = computed(() => props.campaign.channels.reduce((sum, ch) => sum + costFor(ch), 0));

    const setScheduleType = (type: string) => {
      const schedule: CampaignSchedule =
        type === "later"
          ? { type: "later", send_at: new Date(Date.now() + 86_400_000).toISOString(), send_in_customer_timezone: false }
          : type === "recurring"
            ? { type: "recurring", frequency: "weekly", weekday: 1, time: "10:00" }
            : { type: "now" };
      props.onChange({ schedule });
    };

    const Schedule = () => {
      const s = props.campaign.schedule;
      return (
        <section class="space-y-3 rounded-2xl border border-base-300 bg-base-100 p-5">
          <h3 class="font-semibold">{t("campaigns.studio_review.when")}</h3>
          <Segmented
            value={s.type}
            options={[
              { value: "now", label: t("campaigns.studio_review.send_now"), icon: "send" },
              { value: "later", label: t("campaigns.studio_review.schedule"), icon: "schedule" },
              { value: "recurring", label: t("campaigns.studio_review.recurring"), icon: "repeat" },
            ]}
            onChange={setScheduleType}
          />
          {s.type === "later" && (
            <div class="flex flex-wrap items-end gap-4">
              <Field label={t("campaigns.studio_review.send_at")}>
                <input
                  type="datetime-local"
                  class="input input-bordered input-sm"
                  value={toLocalInput(s.send_at)}
                  onInput={(e) =>
                    props.onChange({ schedule: { ...s, send_at: new Date((e.target as HTMLInputElement).value).toISOString() } })
                  }
                />
              </Field>
              <label class="label cursor-pointer gap-2">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary checkbox-sm"
                  checked={s.send_in_customer_timezone}
                  onChange={(e) => props.onChange({ schedule: { ...s, send_in_customer_timezone: (e.target as HTMLInputElement).checked } })}
                />
                <span class="label-text">{t("campaigns.studio_review.customer_timezone")}</span>
              </label>
            </div>
          )}
          {s.type === "recurring" && (
            <div class="grid gap-3 sm:grid-cols-3">
              <Field label={t("campaigns.studio_review.frequency")}>
                <select
                  class="select select-bordered select-sm"
                  value={s.frequency}
                  onChange={(e) => props.onChange({ schedule: { ...s, frequency: (e.target as HTMLSelectElement).value as "weekly" | "monthly" } })}
                >
                  <option value="weekly">{t("campaigns.studio_review.weekly")}</option>
                  <option value="monthly">{t("campaigns.studio_review.monthly")}</option>
                </select>
              </Field>
              <Field label={t("campaigns.studio_review.weekday")}>
                <select
                  class="select select-bordered select-sm"
                  value={String(s.weekday)}
                  onChange={(e) => props.onChange({ schedule: { ...s, weekday: Number((e.target as HTMLSelectElement).value) } })}
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d} value={String(d)}>
                      {new Date(2024, 0, 7 + d).toLocaleDateString(undefined, { weekday: "long" })}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("campaigns.studio_review.time")}>
                <input
                  type="time"
                  class="input input-bordered input-sm"
                  value={s.time}
                  onInput={(e) => props.onChange({ schedule: { ...s, time: (e.target as HTMLInputElement).value } })}
                />
              </Field>
              <p class="text-xs text-base-content/50 sm:col-span-3">{t("campaigns.studio_review.recurring_hint")}</p>
            </div>
          )}
        </section>
      );
    };

    const Checks = () => (
      <section class="space-y-2 rounded-2xl border border-base-300 bg-base-100 p-5">
        <h3 class="font-semibold">{t("campaigns.studio_review.checks")}</h3>
        <ul class="divide-y divide-base-200">
          {checks.value.map((c, i) => (
            <li key={i} class="flex items-start gap-3 py-2 text-sm">
              <span class={["material-symbols-outlined text-lg", LEVEL_ICON[c.level].tone]}>{LEVEL_ICON[c.level].icon}</span>
              <span class="flex-1">
                {c.channel && <span class="badge badge-ghost badge-sm mr-2">{t(CHANNEL_META[c.channel].label)}</span>}
                {t(`campaigns.studio_checks.${c.key}`, c.params)}
              </span>
              {c.level !== "ok" && stepForCheck(c) !== null && (
                <button type="button" class="btn btn-ghost btn-xs" onClick={() => props.onGoTo(stepForCheck(c)!)}>
                  {t("campaigns.studio_review.fix")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    );

    const Summary = () => (
      <aside class="space-y-4">
        <section class="space-y-3 rounded-2xl border border-base-300 bg-base-100 p-5">
          <h3 class="font-semibold">{t("campaigns.studio_review.summary")}</h3>
          <dl class="space-y-2 text-sm">
            <div class="flex justify-between gap-2">
              <dt class="text-base-content/60">{t("campaigns.studio_review.audience")}</dt>
              <dd class="text-right font-medium">{segment.value.name}</dd>
            </div>
            <div class="flex justify-between gap-2">
              <dt class="text-base-content/60">{t("campaigns.studio_review.people")}</dt>
              <dd class="font-medium tabular-nums">{reach.value.people.toLocaleString("de-CH")}</dd>
            </div>
            <div class="flex justify-between gap-2">
              <dt class="text-base-content/60">{t("campaigns.studio_review.offer")}</dt>
              <dd class="text-right font-medium">{offerText(props.campaign.offer) || "—"}</dd>
            </div>
          </dl>
          <div class="divider my-0" />
          {STUDIO_CHANNELS.filter((ch) => props.campaign.channels.includes(ch)).map((ch) => (
            <div key={ch} class="flex items-center justify-between text-sm">
              <span class="flex items-center gap-2">
                <span class={["material-symbols-outlined text-base", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
                {t("campaigns.studio_review.messages", [(reach.value.messages[ch] ?? 0).toLocaleString("de-CH"), t(CHANNEL_META[ch].label)])}
              </span>
              <span class="tabular-nums text-base-content/60">{formatMoney(costFor(ch))}</span>
            </div>
          ))}
          <div class="flex items-center justify-between border-t border-base-200 pt-2 font-semibold">
            <span>{t("campaigns.studio_review.estimated_cost")}</span>
            <span class="tabular-nums">{formatMoney(totalCost.value)}</span>
          </div>
        </section>

        <section class="space-y-2 rounded-2xl border border-base-300 bg-base-100 p-5">
          <h3 class="font-semibold">{t("campaigns.studio_review.test_send")}</h3>
          <p class="text-xs text-base-content/60">{t("campaigns.studio_review.test_send_hint")}</p>
          <div class="join w-full">
            <input
              type="text"
              class="input join-item input-bordered input-sm min-w-0 flex-1"
              placeholder={t("campaigns.studio_review.test_placeholder")}
              value={testRecipient.value}
              onInput={(e) => (testRecipient.value = (e.target as HTMLInputElement).value)}
            />
            <button
              type="button"
              class="btn join-item btn-sm"
              disabled={!testRecipient.value.trim()}
              onClick={() => {
                testSent.value = testRecipient.value.trim();
                testRecipient.value = "";
              }}
            >
              {t("campaigns.studio_review.send_test")}
            </button>
          </div>
          {testSent.value && (
            <div class="text-xs text-success">{t("campaigns.studio_review.test_sent", [testSent.value])}</div>
          )}
        </section>
      </aside>
    );

    return () => (
      <div class="mx-auto grid max-w-6xl gap-6 p-6 lg:grid-cols-[1fr_320px]">
        <div class="space-y-6">
          {Schedule()}
          {Checks()}
        </div>
        {Summary()}
      </div>
    );
  },
});
